import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  effect,
  inject,
  signal,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';

import { Subject, Subscription } from 'rxjs';
import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
  MsalModule,
} from '@azure/msal-angular';
import {
  AuthenticationResult,
  InteractionStatus,
  PopupRequest,
  RedirectRequest,
  EventMessage,
  EventType,
  InteractionType,
  PromptValue,
} from '@azure/msal-browser';
import { IdTokenClaims } from '@azure/msal-common';
import { filter, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { environment } from '../environments/environment';
import { AvailableStore } from '@/store/available.store';
import { PatentStore } from '@/store/patents.store';
import { UserDataStore } from '@/store/user-store/user-data.store';
import { NotificationsStore } from '@/store/notifications.store';
import { LoggerService } from './core/service/logger/logger.service';
import { LoadingPaeComponent } from './shared/loading-pae/loading-pae.component';
import { PortalMessagesTycService } from './core/service/portal-messages-tyc/portal-messages-tyc.service';
import { AlertComponent } from './shared/alert/alert.component';
import { ModalComponent } from './shared/modal/modal.component';
import { ModalActionComponent } from './shared/modal/modal-action.component';
import { PASSWORD_RESET_SUCCESS_KEY } from './app.config';
import { IconName } from './shared/interfaces/icons/icons.type';
import { AnalyticsService } from './core/service/analytics/analytics.service';
import { DiagnosticSessionService } from './core/diagnostics/diagnostic-session.service';

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string;
  tfp?: string;
};

export const apiConfig: { b2cScopes: string[]; webApi: string } = {
  b2cScopes: [
    `${environment.tenant}/${environment.clientId}/user_impersonation`,
    `${environment.tenant}/${environment.clientId}/read`,
    `${environment.tenant}/${environment.clientId}/write`,
  ],
  webApi: `${environment.frontBaseURL}/dashboard`,
};

@Component({
  selector: 'app-root',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterOutlet,
    MsalModule,
    LoadingPaeComponent,
    AlertComponent,
    ModalComponent,
    ModalActionComponent,
  ],
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  //! Servicios
  msalService = inject(MsalService);
  private logger = inject(LoggerService);
  portalMessagesTycService = inject(PortalMessagesTycService);

  //! Stores
  userDataStore = inject(UserDataStore);
  availableStore = inject(AvailableStore);
  patentsStore = inject(PatentStore);
  notificationsStore = inject(NotificationsStore);

  //! Variables y señales
  public messageLoading = signal('Validando autenticación...');
  private msalReady = signal(false);
  public pageReady = signal(false);
  private subActions: Subscription | undefined;
  // public loggedIn: boolean;
  public isIframe = false;
  public loginDisplay = false;
  private readonly destroying$ = new Subject<void>();
  private readonly analytics = inject(AnalyticsService);
  readonly diagnostics = inject(DiagnosticSessionService);
  private fromPassReset = true;
  private passwordResetSuccess = false;
  public alertTitle = signal('');
  public alertMessage = signal('');
  public icon = signal<IconName>('check-circle');
  private hasViewInitialized = signal(false);

  @ViewChild('globalAlert') globalAlert!: AlertComponent;
  @ViewChild('newUserTyCModal') newUserTyCModal!: ModalComponent;
  @ViewChild('notificationModal') notificationModal!: ModalActionComponent;
  @ViewChild('viewAlertMessage') viewAlertMessage!: AlertComponent;

  private readonly LUBES_WARNING_AFTER_LOGIN_KEY = 'showLubesWarningAfterLogin';
  private readonly LUBES_CART_CREATED_THIS_SESSION_KEY = 'lubesCartCreatedThisSession';

  constructor(
    private router: Router,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
  ) {
    this.logger.log(new Date(), 'Hora del SWA');

    effect(() => {
      if (this.msalReady()) {
        this.logger.log('Cargando datos del usuario...', 'LayoutComponente');
        this.messageLoading.set('Cargando datos del usuario...');
        this.userDataStore.mtSession(null);
      }
    });
    //! Carga datos Montos Disponibles
    effect(() => {
      if (this.userDataStore.isLoaded()) {
        this.logger.log('Cargando montos disponibles...', 'LayoutComponente');
        this.messageLoading.set('Cargando montos disponibles...');
        this.availableStore.mtStart(null);
        this.availableStore.mtAccSolicitante(null);
      }
    });

    effect(() => {
      if (this.userDataStore.isLoaded()) {
        this.logger.log('Cargando PBL...', 'LayoutComponente');
        this.userDataStore.mtGetPbl2();
        this.userDataStore.mtGetPbl();
      }
    });

    //! Carga las notificaciones de Nuevos Contenidos
    effect(() => {
      if (this.userDataStore.isLoaded()) {
        this.logger.log('Cargando Nuevos Contenidos...', 'LayoutComponente');
        this.notificationsStore.loadNotifications();
      }
    });

    //! Carga datos las patentes
    effect(() => {
      if (this.availableStore.isLoaded()) {
        this.logger.log('Cargando patentes...', 'LayoutComponente');
        this.messageLoading.set('Cargando patentes...');
        this.patentsStore.mtStart(null);
      }
    });

    //! Quito los mensajes de carga
    effect(() => {
      if (this.userDataStore.isLoaded() && this.availableStore.isLoaded() && this.patentsStore.isLoaded()) {
        this.messageLoading.set('');
        this.pageReady.set(true);
      }
    });

    effect(() => {
      if (!this.hasViewInitialized()) return;
      this.syncGlobalMessages();
    });

    const routerEvents = this.router?.events;
    if (routerEvents) {
      routerEvents
        .pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd),
          takeUntil(this.destroying$),
        )
        .subscribe(() => {
          queueMicrotask(() => this.syncGlobalMessages());
        });
    }
  }

  ngAfterViewInit(): void {
    this.hasViewInitialized.set(true);

    if (this.passwordResetSuccess) {
      queueMicrotask(() => this.showPasswordResetAlert());
    }
  }

  private syncGlobalMessages(): void {
    if (!this.userDataStore.isLoaded()) return;

    this.logger.log(
      {
        pendingUnreadNotification: this.portalMessagesTycService.pendingUnreadNotification(),
        canOpenNotificationModal: this.portalMessagesTycService.canOpenNotificationModal(),
        pendingTyCFlow: this.portalMessagesTycService.pendingTyCFlow(),
        canOpenTyCWarning: this.portalMessagesTycService.canOpenTyCWarning(),
        tycAlertMode: this.portalMessagesTycService.tycAlertMode(),
      },
      '######AppComponent | syncGlobalMessages',
    );

    const pendingTyCFlow = this.portalMessagesTycService.pendingTyCFlow();

    if (pendingTyCFlow === 'primary' && this.portalMessagesTycService.canOpenTyCWarning()) {
      this.portalMessagesTycService.prepareTyCWarningState();
      this.portalMessagesTycService.markTyCWarningPresented();
      queueMicrotask(() => this.globalAlert?.openAlert());
      return;
    }

    const notification = this.portalMessagesTycService.pendingUnreadNotification();
    if (
      !this.portalMessagesTycService.shouldBlockNotifications() &&
      notification &&
      this.portalMessagesTycService.canOpenNotificationModal()
    ) {
      this.portalMessagesTycService.prepareNotificationModal();
      queueMicrotask(() => {
        if (!this.notificationModal) return;
        this.notificationModal.cantBeClosed = true;
        this.notificationModal.openModal();
      });
      return;
    }

    if (this.portalMessagesTycService.canOpenTyCWarning()) {
      this.portalMessagesTycService.prepareTyCWarningState();
      this.portalMessagesTycService.markTyCWarningPresented();
      queueMicrotask(() => this.globalAlert?.openAlert());
    }
  }

  public async onTyCActionClicked(action: string): Promise<void> {
    const result = await this.portalMessagesTycService.handleTyCAction(action);

    if (result === 'open-new-user-modal') {
      this.newUserTyCModal?.openModal();
      return;
    }

    if (result === 'reload-warning') {
      if (this.portalMessagesTycService.prepareTyCWarningState()) {
        this.portalMessagesTycService.markTyCWarningPresented();
        queueMicrotask(() => this.globalAlert?.openAlert());
      }
      return;
    }

    if (result === 'show-alert') {
      queueMicrotask(() => this.globalAlert?.openAlert());
    }
  }

  public async onNewUserTyCAction(action: string): Promise<void> {
    if (action !== 'approve') return;

    this.newUserTyCModal.closeModal();
    await this.portalMessagesTycService.approveNewUserTyC();
    queueMicrotask(() => this.globalAlert?.openAlert());
  }

  public async onNotificationNotified(): Promise<void> {
    const pending = this.portalMessagesTycService.pendingUnreadNotification();
    if (!pending) return;

    const ok = await this.portalMessagesTycService.markNotificationAsRead(pending.id);
    if (ok) {
      this.notificationModal.closeModal();
    }
  }

  ngOnInit(): void {
    this.analytics.init();
    this.isIframe = window !== window.parent && !window.opener;

    this.passwordResetSuccess = sessionStorage.getItem(PASSWORD_RESET_SUCCESS_KEY) === 'true';
    if (this.passwordResetSuccess) {
      sessionStorage.removeItem(PASSWORD_RESET_SUCCESS_KEY);
      // Keep the application on an unprotected route while the modal is open.
      this.pageReady.set(true);
      void this.router.navigateByUrl('/password-reset-success', { replaceUrl: true });
    }

    // Keep the guard and the root component synchronized when the redirect
    // was processed during the application initializer.
    this.msalBroadcastService.resetInProgressEvent();
    this.setLoginDisplay();

    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.ACTIVE_ACCOUNT_CHANGED))
      .subscribe(() => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroying$),
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
        this.markMsalReadyIfAuthenticated();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS,
        ),
        takeUntil(this.destroying$),
      )
      .subscribe((result: EventMessage) => {
        if (result.eventType === EventType.LOGIN_SUCCESS) {
          sessionStorage.setItem(this.LUBES_WARNING_AFTER_LOGIN_KEY, 'true');
          sessionStorage.removeItem(this.LUBES_CART_CREATED_THIS_SESSION_KEY);
        }

        const payload = result.payload as AuthenticationResult;
        const idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

        if (idtoken.acr === environment.signUpSignIn || idtoken.tfp === environment.signUpSignIn) {
          this.authService.instance.setActiveAccount(payload.account);
          this.redirectFromPassReset();
        }

        if (
          idtoken.acr?.toLowerCase() === environment.resetPassword.toLowerCase() ||
          idtoken.tfp?.toLowerCase() === environment.resetPassword.toLowerCase()
        ) {
          const signUpSignInFlowRequest: RedirectRequest | PopupRequest = {
            authority: `${environment.authority}/${environment.signUpSignIn}`,
            scopes: [...apiConfig.b2cScopes],
            prompt: PromptValue.LOGIN,
          };

          this.login(signUpSignInFlowRequest);
        }

        return result;
      });

    // The MSAL initializer has already completed before AppComponent is
    // created. Initialize the application state explicitly as well, so the
    // loader does not depend on receiving the initial BehaviorSubject value.
    this.checkAndSetActiveAccount();
    this.setLoginDisplay();
    this.markMsalReadyIfAuthenticated();
  }

  private markMsalReadyIfAuthenticated(): void {
    if (this.authService.instance.getAllAccounts().length > 0) {
      this.msalReady.set(true);
    }
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  loginRedirect(redirectStartPage?: string) {
    const redirectRequest = {
      ...this.msalGuardConfig.authRequest,
      ...(redirectStartPage ? { redirectStartPage } : {}),
    } as RedirectRequest;

    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect(redirectRequest);
    } else {
      this.authService.loginRedirect(redirectStartPage ? redirectRequest : undefined);
    }
  }

  login(userFlowRequest?: RedirectRequest | PopupRequest) {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        this.authService
          .loginPopup({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as PopupRequest)
          .subscribe((response: AuthenticationResult) => {
            this.authService.instance.setActiveAccount(response.account);
          });
      } else {
        this.authService.loginPopup(userFlowRequest).subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        });
      }
    } else if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as RedirectRequest);
    } else {
      this.authService.loginRedirect(userFlowRequest);
    }
  }

  redirectFromPassReset() {
    if (this.fromPassReset) {
      this.fromPassReset = false;
      this.router.navigateByUrl('/portal/home');
    }
  }

  public onPasswordResetAction(action: string): void {
    if (action !== 'accept') return;

    this.loginRedirect(`${window.location.origin}/portal/home`);
  }

  private showPasswordResetAlert(): void {
    this.alertTitle.set('¡Operación exitosa!');
    this.alertMessage.set('Contraseña modificada.\nPodés iniciar sesión con nueva contraseña.');
    this.icon.set('check-circle');
    this.viewAlertMessage.openAlert();
  }

  ngOnDestroy(): void {
    this.subActions?.unsubscribe();
    this.destroying$.next(undefined);
    this.destroying$.complete();
  }

  public async onGlobalAlertClicked(action: string): Promise<void> {
    if (action === 'acceptAndClose') {
      this.portalMessagesTycService.closeTyCAlert();
      queueMicrotask(() => this.syncGlobalMessages());
      return;
    }

    await this.onTyCActionClicked(action);
  }
}
